import React, { useState, useEffect } from 'react';
import axios from 'axios';

function HackathonCountdown() {
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [timeRemaining, setTimeRemaining] = useState({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
    });
    const [statusMessage, setStatusMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);
    const [countdownState, setCountdownState] = useState('not_started'); // not_started, ongoing, ended

    // Fetch hackathon times from API
    useEffect(() => {
        axios.get('/api/get_hackathon_time/')
            .then(({ data }) => {
                const start = new Date(data.start_time_utc + 'Z'); // Ensure UTC with 'Z'
                const end = new Date(data.end_time_utc + 'Z');
                setStartTime(formatDateUTC(start));
                setEndTime(formatDateUTC(end));
                updateStatus(start, end);
                setIsLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching hackathon time:", err);
                setError(true);
                setIsLoading(false);
            });
    }, []);

    // Update countdown every second
    useEffect(() => {
        if (!startTime || !endTime) return;
        
        const interval = setInterval(() => {
            const start = new Date(startTime + 'Z');
            const end = new Date(endTime + 'Z');
            updateStatus(start, end);
        }, 1000);
        
        return () => clearInterval(interval);
    }, [startTime, endTime]);

    // Update hackathon status and remaining time
    const updateStatus = (start, end) => {
        const now = new Date();
        const utcNow = new Date(now.getTime() + now.getTimezoneOffset() * 60000); // Convert to UTC

        if (utcNow < start) {
            setStatusMessage('Hackathon has not started yet');
            setCountdownState('not_started');
            calculateRemainingTime(start - utcNow);
        } else if (utcNow >= end) {
            setStatusMessage('Hackathon has ended');
            setCountdownState('ended');
            setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        } else {
            setStatusMessage('Hackathon is ongoing!');
            setCountdownState('ongoing');
            calculateRemainingTime(end - utcNow);
        }
    };

    // Calculate remaining time components
    const calculateRemainingTime = (diff) => {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        
        setTimeRemaining({ days, hours, minutes, seconds });
    };

    // Format date to "YYYY-MM-DD HH:MM:SS" in UTC
    const formatDateUTC = (date) => {
        const pad = (num) => String(num).padStart(2, '0');
        return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
    };

    // Function to format date to a more readable format
    const formatReadableDate = (dateStr) => {
        if (!dateStr) return '';
        
        const date = new Date(dateStr + 'Z');
        const options = { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit',
            timeZone: 'UTC',
            timeZoneName: 'short'
        };
        
        return date.toLocaleDateString('en-US', options);
    };

    // Render a single time unit (days, hours, etc.)
    const renderTimeUnit = (value, label) => (
        <div className="flex flex-col items-center mx-2 md:mx-4">
            <div className="bg-secondary text-white text-2xl md:text-3xl font-bold rounded-lg w-14 h-14 md:w-20 md:h-20 flex items-center justify-center">
                {value}
            </div>
            <span className="text-xs md:text-sm mt-2 text-gray-600 dark:text-gray-400 font-medium">{label}</span>
        </div>
    );

    if (isLoading) {
        return (
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-secondary"></div>
                <span className="ml-3 text-gray-700 dark:text-gray-300">Loading hackathon details...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 dark:bg-gray-800 border-l-4 border-red-500 p-6 rounded-lg shadow-lg">
                <div className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-red-500 font-medium">Failed to load hackathon details</p>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mt-2">Please try refreshing the page or contact support if the problem persists.</p>
            </div>
        );
    }

    return (
        <section className="py-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                <div className={`p-4 ${
                    countdownState === 'not_started' ? 'bg-blue-600' : 
                    countdownState === 'ongoing' ? 'bg-green-600' : 'bg-gray-600'
                }`}>
                    <h2 className="text-xl md:text-2xl font-bold text-white flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Hackathon Countdown
                    </h2>
                </div>
                
                <div className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
                        <div className="mb-4 md:mb-0">
                            <div className="flex items-center">
                                <div className={`w-3 h-3 rounded-full mr-2 ${
                                    countdownState === 'not_started' ? 'bg-blue-500' : 
                                    countdownState === 'ongoing' ? 'bg-green-500' : 'bg-gray-500'
                                }`}></div>
                                <span className="font-bold text-lg text-gray-900 dark:text-white">{statusMessage}</span>
                            </div>
                            
                            <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                <span><strong>Start:</strong> {formatReadableDate(startTime)}</span>
                                <span className="mx-2">|</span>
                                <span><strong>End:</strong> {formatReadableDate(endTime)}</span>
                            </div>
                        </div>
                        
                        {countdownState !== 'ended' && (
                            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                <span className="mr-2 font-medium">
                                    {countdownState === 'not_started' ? 'Starts in:' : 'Ends in:'}
                                </span>
                            </div>
                        )}
                    </div>
                    
                    {countdownState !== 'ended' && (
                        <div className="flex justify-center items-center py-2">
                            {renderTimeUnit(timeRemaining.days, 'Days')}
                            {renderTimeUnit(timeRemaining.hours, 'Hours')}
                            {renderTimeUnit(timeRemaining.minutes, 'Minutes')}
                            {renderTimeUnit(timeRemaining.seconds, 'Seconds')}
                        </div>
                    )}
                    
                    {countdownState === 'ended' && (
                        <div className="text-center py-4">
                            <p className="text-lg text-gray-600 dark:text-gray-400">Thank you for participating!</p>
                            <p className="text-gray-500 dark:text-gray-500 mt-1">Results will be announced soon.</p>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

export default HackathonCountdown;